import React, { useRef } from 'react';
import { useDispatch } from 'react-redux';
import { FilterToolbar, LensTheme } from '@diligentcorp/atlas-theme-mui-lens';
import { StyledIcon } from 'src/components/foundations/foundations.styles';
import { ColumnType, OnRowClickType } from 'src/components/molecules';
import {
  Box,
  Checkbox,
  CircularProgress,
  FormControl,
  FormLabel,
  InputAdornment,
  ListItemText,
  MenuItem,
  Select,
  SelectChangeEvent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  useTheme,
} from '@mui/material';
import { useTheme as useStyledTheme } from 'styled-components';
import {
  searchQueryUpdated,
  selectedStatusesUpdated,
} from './CoiFormManagement.slice';
import {
  FormsResetFiltersButton,
  FormsResetFiltersButtonText,
  FormsSection,
  FormsSectionHeader,
} from './CoiFormManagement.styles';
import { FormsTableEmptyState } from './FormsTableEmptyState';
import {
  FormStatusFilter,
  FormStatusTab,
  STATUS_FILTER_OPTIONS,
} from './types';
import { useFormManagementActionsColumnScrollActive } from './useFormManagementActionsColumnScrollActive';

export interface FormsListSectionProps {
  selectedTab: FormStatusTab;
  searchQuery: string;
  filtersActive: boolean;
  onResetFilters: () => void;
  selectedStatuses: FormStatusFilter[];
  isLoading: boolean;
  tableKey: string;
  columns: ColumnType[];
  columnWidths: (string | undefined)[];
  sortableColumns: string[];
  tableData: { row: string; cellData: (string | number | React.ReactNode)[] }[];
  effectivePage: number;
  onPageChange: (page: number) => void;
  sortedColumn: { columnName: string; direction: 'asc' | 'desc' } | undefined;
  onColumnSort: (columnName: string, direction: 'asc' | 'desc') => void;
  itemsPerPage: number;
  onItemsPerPageChange: (option: { value: number; label: string }) => void;
  totalCount: number;
  organisationFormsCount: number;
  onRowClick?: OnRowClickType<{
    row: string;
    cellData: (string | number | React.ReactNode)[];
  }>;
}

export const FormsListSection = ({
  selectedTab,
  searchQuery,
  filtersActive,
  onResetFilters,
  selectedStatuses,
  isLoading,
  tableKey,
  columns,
  columnWidths,
  sortableColumns,
  tableData,
  effectivePage,
  onPageChange,
  sortedColumn,
  onColumnSort,
  itemsPerPage,
  onItemsPerPageChange,
  totalCount,
  organisationFormsCount,
  onRowClick,
}: FormsListSectionProps) => {
  const dispatch = useDispatch();
  const stickyTableWrapperRef = useRef<HTMLDivElement>(null);
  const styledTheme = useStyledTheme();
  const { presets } = useTheme<LensTheme>();
  const AtlasTableSortLabel =
    presets.TablePresets.components.AtlasTableSortLabel;

  useFormManagementActionsColumnScrollActive(
    stickyTableWrapperRef,
    selectedTab,
    tableKey,
  );

  if (!isLoading && organisationFormsCount === 0) {
    return null;
  }

  return (
    <FormsSection data-testid="forms-section">
      <FormsSectionHeader data-testid="forms-header">
        <h3>All forms</h3>
        {/* TODO: Active/Draft toggle temporarily removed
        <FormsTabsRow data-testid="forms-tabs-row">
          <ButtonGroup
            options={[FormStatusTab.ACTIVE, FormStatusTab.DRAFTS]}
            selectedOption={selectedTab}
            setSelectedOption={onTabChange}
            buttonGroupOptionTestId="form-status-tabs"
            size="small"
          />
        </FormsTabsRow>
        */}
      </FormsSectionHeader>
      <FilterToolbar
        data-testid="forms-table-toolbar"
        search={
          <FormControl>
            <FormLabel htmlFor="forms-global-search">Search</FormLabel>
            <TextField
              placeholder="Search by all fields"
              value={searchQuery}
              onChange={(e) => dispatch(searchQueryUpdated(e.target.value))}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <StyledIcon className="uil uil-search" size="20px" />
                    </InputAdornment>
                  ),
                  id: 'forms-global-search',
                },
              }}
              inputProps={{ 'data-testid': 'forms-global-search' }}
              data-testid="forms-search-input-wrapper"
            />
          </FormControl>
        }
        dropdowns={
          <FormControl sx={{ minWidth: 204, width: 204 }}>
            <FormLabel id="forms-status-filter-label">Status</FormLabel>
            <Select<FormStatusFilter[]>
              labelId="forms-status-filter-label"
              multiple
              displayEmpty
              value={selectedStatuses}
              onChange={(e: SelectChangeEvent<FormStatusFilter[]>) => {
                const val = e.target.value;
                dispatch(
                  selectedStatusesUpdated(
                    (typeof val === 'string'
                      ? [val as FormStatusFilter]
                      : val) as FormStatusFilter[],
                  ),
                );
              }}
              renderValue={(selected) =>
                selected.length === 0 ? 'Filter by status' : selected.join(', ')
              }
              data-testid="forms-filter-by-status"
              inputProps={{ 'aria-label': 'Filter by status' }}
            >
              {STATUS_FILTER_OPTIONS.map((opt) => (
                <MenuItem key={opt} value={opt}>
                  <Checkbox
                    checked={selectedStatuses.includes(opt)}
                    size="small"
                  />
                  <ListItemText primary={opt} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        }
        buttons={
          filtersActive ? (
            <FormsResetFiltersButton
              type="button"
              onClick={onResetFilters}
              data-testid="forms-reset-filters"
              aria-label="Reset filters"
            >
              <StyledIcon
                className="uil uil-redo"
                size="16px"
                color={
                  styledTheme.isVaultVariant
                    ? styledTheme.newPalette.neutral.N800
                    : styledTheme.atlas.semantic.color.type.default
                }
              />
              <FormsResetFiltersButtonText>
                Reset filters
              </FormsResetFiltersButtonText>
            </FormsResetFiltersButton>
          ) : undefined
        }
      />
      {isLoading ? (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '200px',
          }}
          data-testid="forms-table-loading-overlay"
          role="status"
          aria-label="Loading forms"
        >
          <CircularProgress />
        </Box>
      ) : (
        <Box
          ref={stickyTableWrapperRef}
          sx={{ position: 'relative' }}
          data-testid="forms-table-wrapper"
        >
          <TableContainer
            data-testid={`forms-table-${selectedTab.toLowerCase()}-container`}
            sx={{ position: 'relative', overflowX: 'auto' }}
          >
            <Table
              data-testid={`forms-table-${selectedTab.toLowerCase()}`}
              sx={{ tableLayout: 'auto', minWidth: 900 }}
            >
              <TableHead>
                <TableRow
                  sx={{
                    backgroundColor:
                      styledTheme.atlas.semantic.color.surface.variant,
                  }}
                >
                  {columns.map((col, i) => {
                    const isSortable = sortableColumns.includes(col.label);
                    const isActive = sortedColumn?.columnName === col.label;
                    const isLast = i === columns.length - 1;
                    return (
                      <TableCell
                        key={col.label}
                        sx={{
                          minWidth: columnWidths[i],
                          width: columnWidths[i],
                          backgroundColor:
                            styledTheme.atlas.semantic.color.surface.variant,
                          fontWeight: 600,
                          ...(isLast && {
                            position: 'sticky',
                            right: 0,
                            zIndex: 2,
                            textAlign: 'center',
                            borderRadius: 0,
                            boxShadow:
                              '-8px 0 16px -4px rgba(0,0,0,0.2), -2px 0 4px 0 rgba(0,0,0,0.15)',
                          }),
                        }}
                        sortDirection={
                          isActive ? sortedColumn?.direction : false
                        }
                      >
                        {isSortable ? (
                          <AtlasTableSortLabel
                            active={isActive}
                            direction={
                              isActive ? sortedColumn?.direction : 'asc'
                            }
                            onClick={() =>
                              onColumnSort(
                                col.label,
                                isActive && sortedColumn?.direction === 'asc'
                                  ? 'desc'
                                  : 'asc',
                              )
                            }
                            sortedAscendingLabel="Sorted ascending"
                            sortedDescendingLabel="Sorted descending"
                          >
                            {col.label}
                          </AtlasTableSortLabel>
                        ) : (
                          col.label
                        )}
                      </TableCell>
                    );
                  })}
                </TableRow>
              </TableHead>
              <TableBody>
                {tableData.length === 0 && !isLoading ? (
                  <TableRow>
                    <TableCell
                      colSpan={columns.length}
                      sx={{ border: 0, p: 0 }}
                    >
                      <FormsTableEmptyState
                        message={
                          filtersActive
                            ? `No data present for your search`
                            : `No ${selectedTab.toLowerCase()} forms`
                        }
                      />
                    </TableCell>
                  </TableRow>
                ) : (
                  tableData.map((row) => (
                    <TableRow
                      key={row.row}
                      hover
                      sx={{ cursor: 'pointer' }}
                      onClick={(e) => onRowClick?.(row.row, row, e)}
                      data-testid={`forms-table-row-${row.row}`}
                    >
                      {row.cellData.map((cell, i) => {
                        const isLast = i === row.cellData.length - 1;
                        return (
                          <TableCell
                            key={columns[i]?.label ?? i}
                            sx={
                              isLast
                                ? {
                                    position: 'sticky',
                                    right: 0,
                                    zIndex: 1,
                                    backgroundColor:
                                      styledTheme.atlas.semantic.color.surface
                                        .default,
                                    textAlign: 'center',
                                    boxShadow:
                                      '-8px 0 16px -4px rgba(0,0,0,0.2), -2px 0 4px 0 rgba(0,0,0,0.15)',
                                  }
                                : undefined
                            }
                            onClick={
                              isLast ? (e) => e.stopPropagation() : undefined
                            }
                          >
                            {cell}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}
      {totalCount > 0 && (
        <TablePagination
          component="div"
          count={totalCount}
          page={effectivePage - 1}
          onPageChange={(_, p) => onPageChange(p + 1)}
          rowsPerPage={itemsPerPage}
          onRowsPerPageChange={(e) =>
            onItemsPerPageChange({
              value: +e.target.value,
              label: e.target.value,
            })
          }
          rowsPerPageOptions={[10, 25, 50, 100]}
          labelRowsPerPage="Rows"
        />
      )}
    </FormsSection>
  );
};
